import { createEffect, untrack } from "solid-js"
import { Group, Mesh, type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import type { MediumGunAntiAirEntity } from "../../core/entity/EntityType/MediumGunAntiAir"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { M247SgtYork1_M247SgtYork1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseGeometryNormalsByMeshNameList from "./creaseGeometryNormalsByMeshNameList"
import cubicEaseInOutBounce from "./cubicEaseInOutBounce"
import runWithObject3DOwner from "./runWithObject3DOwner"

const totalLoopMs = 1000

// const heights = [1, 2, 2, 3, 3, 4]

const meshesToCrease = [
  'Barrel',
  'BarrelJacket',
]

export default function makeMediumGunAntiAirScene (
  engine: Engine,
  ent: EntityGraphNode<MediumGunAntiAirEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(M247SgtYork1_M247SgtYork1_GLTF_Model, function (ModelObj) {
    tmpRng.seed = ent.id
    const { scene } = ModelObj
    // printObjectHierarchy(scene)

    const bodyMesh = scene.getObjectByName('Body') as Mesh
    assertClassInDev(bodyMesh, Mesh)

    const turretGroup = scene.getObjectByName('Turret') as Group
    assertClassInDev(turretGroup, Group)

    const barrelMesh = scene.getObjectByName('Barrel') as Mesh
    assertClassInDev(barrelMesh, Mesh)

    // turretGroup.position.y -= 2
    // turretGroup.position.y = 2.5
    // barrelMesh.position.y = 0.7
    // const landToLandAttackBarrelRotationX = barrelMesh.rotation.x
    // console.log('landToLandAttackBarrelRotationX', landToLandAttackBarrelRotationX)
    barrelMesh.rotateX(-PI / 7)
    // const landToAirttackBarrelRotationX = barrelMesh.rotation.x
    // console.log('landToAirttackBarrelRotationX', landToAirttackBarrelRotationX)

    // bodyMesh.add(turretGroup)
    // turretGroup.add(barrelMesh)
    // scene.add(bodyMesh)

    const leftTrackMesh = scene.getObjectByName('LeftTrack') as Mesh
    assertClassInDev(leftTrackMesh, Mesh)
    // console.log('leftTrackMesh', leftTrackMesh)

    const rightTrackMesh = leftTrackMesh.clone()
    rightTrackMesh.name = 'RightTrack'

    // scene.add(leftTrackMesh)
    scene.add(rightTrackMesh)

    // rightTrackMesh.position.x = 10
    // rightTrackMesh.position.x *= -1
    rightTrackMesh.scale.x *= -1

    applyPlayerColors(engine, scene, ent)

    const defaultHullY = bodyMesh.position.y
    // const defaultHullX = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })

    mountOnFrameUpdateToObject3D(engine, bodyMesh, (elapsedMs: number, frameAt: number): void => {
      const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyMesh.position.y = defaultHullY
      } else if (entValue.player_id) {
        bodyMesh.position.y = defaultHullY + (cubicEaseInOutBounce(mod / 500) / 7)
      }

      // barrelMesh.rotation.x = Math.random() > 0.5 ? landToAirttackBarrelRotationX
      //   : landToLandAttackBarrelRotationX

      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    creaseGeometryNormalsByMeshNameList(scene, meshesToCrease)

    callback(scene)
  })
}
